/**
 * Weekly Cashflow Component
 * Displays weekly financial data with KPIs and matrix visualization
 * KPIs: Total Income, Total Expense, Total Net Income (current & last week)
 * Matrix: Parent Category > Category with weekly values
 * Supports consolidated view (all user companies aggregated) or single company view
 */
import { useState, useEffect, useRef } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import html2pdf from 'html2pdf.js';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import '../styles/WeeklyDataMatrix.css';

function WeeklyDataMatrix() {
  const [companies, setCompanies] = useState([]);
  const [parentCompanies, setParentCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedParentCompany, setSelectedParentCompany] = useState('');
  const matrixScrollRef = useRef(null);
  const balanceSummaryScrollRef = useRef(null);
  const [weeklyData, setWeeklyData] = useState([]);
  const [filteredWeeklyData, setFilteredWeeklyData] = useState([]);
  const [matrixData, setMatrixData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [showDetailedBalance, setShowDetailedBalance] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('consolidated'); // 'consolidated' or 'single'
  const [period, setPeriod] = useState('w'); // 'w' (weekly) or 'm' (monthly)

  // Filters for end_date
  const [selectedWeekEnds, setSelectedWeekEnds] = useState([]); // Changed to array for multi-select
  const [lastNWeeks, setLastNWeeks] = useState('all');

  useEffect(() => {
    loadCompanies();
  }, []);

  // Reload data when period changes (if data was already loaded)
  useEffect(() => {
    if (weeklyData.length > 0) {
      loadWeeklyData();
    }
  }, [period]);

  // Auto-scroll matrix to the right to show most recent weeks
  useEffect(() => {
    if (matrixScrollRef.current && matrixData) {
      setTimeout(() => {
        matrixScrollRef.current.scrollLeft = matrixScrollRef.current.scrollWidth;
      }, 100);
    }
  }, [matrixData]);

  // Auto-scroll balance summary to the right to show most recent weeks
  useEffect(() => {
    if (balanceSummaryScrollRef.current && balanceSummary) {
      setTimeout(() => {
        balanceSummaryScrollRef.current.scrollLeft = balanceSummaryScrollRef.current.scrollWidth;
      }, 100);
    }
  }, [balanceSummary]);

  const loadCompanies = async () => {
    try {
      const [companiesRes, parentCompaniesRes] = await Promise.all([
        api.get('/data/my-companies'),
        api.get('/parent-companies')
      ]);

      setCompanies(companiesRes.data);
      setParentCompanies(parentCompaniesRes.data);

      // Auto-select first company if available
      if (companiesRes.data.length > 0) {
        setSelectedCompany(companiesRes.data[0].company_id);
      }

      // Auto-select first parent company if available
      if (parentCompaniesRes.data.length > 0) {
        setSelectedParentCompany(parentCompaniesRes.data[0].parent_company_id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
      alert('Failed to load companies');
    }
  };

  const loadWeeklyData = async () => {
    if (viewMode === 'single' && !selectedCompany) {
      alert('Please select a company');
      return;
    }

    if (viewMode === 'consolidated' && !selectedParentCompany) {
      alert('Please select a parent company');
      return;
    }

    setLoading(true);
    try {
      let url = '/data/weekly-data';

      if (viewMode === 'single' && selectedCompany) {
        url += `?company_id=${selectedCompany}&period=${period}`;
      } else if (viewMode === 'consolidated' && selectedParentCompany) {
        url += `?parent_company_id=${selectedParentCompany}&period=${period}`;
      }

      const response = await api.get(url);
      setWeeklyData(response.data);
      setFilteredWeeklyData(response.data);

      // Reset filters when loading new data
      setSelectedWeekEnds([]);
      setLastNWeeks('all');

      transformToMatrix(response.data);
      calculateKPIs(response.data);
      prepareTrendData(response.data);
      calculateBalanceSummary(response.data);
    } catch (error) {
      console.error('Failed to load weekly data:', error);
      alert('Failed to load weekly data');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters whenever filter state changes
  useEffect(() => {
    applyFilters();
  }, [selectedWeekEnds, lastNWeeks, weeklyData]);

  const applyFilters = () => {
    console.log('🔍 Applying filters...', { selectedWeekEnds, lastNWeeks });

    if (!weeklyData || weeklyData.length === 0) {
      console.log('❌ No weekly data available');
      setFilteredWeeklyData([]);
      setKpis(null);
      setMatrixData(null);
      setTrendData([]);
      return;
    }

    let filtered = [...weeklyData];
    console.log('📊 Starting with', filtered.length, 'records');

    // Filter by specific end_dates (multi-select)
    if (selectedWeekEnds.length > 0) {
      filtered = filtered.filter(record => selectedWeekEnds.includes(record.end_date));
      console.log('✂️ After week filter:', filtered.length, 'records');
    }

    // Filter by last N weeks
    if (lastNWeeks !== 'all') {
      const uniqueWeekEnds = getUniqueWeekEnds();
      const lastNWeekEnds = uniqueWeekEnds.slice(0, parseInt(lastNWeeks));
      console.log('📅 Last N weeks filter:', lastNWeekEnds);
      filtered = filtered.filter(record => lastNWeekEnds.includes(record.end_date));
      console.log('✂️ After last N weeks filter:', filtered.length, 'records');
    }

    // Update filtered data and recalculate everything based on filtered data
    setFilteredWeeklyData(filtered);

    // If no data after filtering, clear everything
    if (filtered.length === 0) {
      console.log('⚠️ No data after filtering - clearing all visualizations');
      setKpis(null);
      setMatrixData(null);
      setTrendData([]);
      setBalanceSummary(null);
    } else {
      // Recalculate all KPIs and visualizations with filtered data
      console.log('✅ Recalculating visualizations with', filtered.length, 'filtered records');
      transformToMatrix(filtered);
      calculateKPIs(filtered);
      prepareTrendData(filtered);
      calculateBalanceSummary(filtered);
    }
  };

  // Get unique week end dates from weekly data (sorted newest first)
  const getUniqueWeekEnds = () => {
    if (!weeklyData || weeklyData.length === 0) return [];
    const uniqueDates = [...new Set(weeklyData.map(record => record.end_date))];
    return uniqueDates.sort((a, b) => new Date(b) - new Date(a));
  };

  const calculateKPIs = (data) => {
    console.log('🔄 Calculating KPIs with', data.length, 'records');

    if (!data || data.length === 0) {
      console.log('❌ No data for KPIs');
      setKpis(null);
      return;
    }

    // Get unique end_date values and sort descending (most recent first)
    const uniqueWeekEndDates = [...new Set(data.map(row => row.end_date))];
    const sortedWeeks = uniqueWeekEndDates.sort((a, b) => new Date(b) - new Date(a));

    console.log('📅 Available weeks:', sortedWeeks);

    if (sortedWeeks.length === 0) {
      console.log('❌ No weeks found');
      setKpis(null);
      return;
    }

    // Current week is the most recent end_date
    const currentWeekEndDate = sortedWeeks[0];
    // Last week is the second most recent end_date (if exists)
    const lastWeekEndDate = sortedWeeks.length > 1 ? sortedWeeks[1] : null;

    console.log('📊 Current Week:', currentWeekEndDate, '| Last Week:', lastWeekEndDate);

    // Calculate TOTAL for ALL filtered data (not just current week)
    console.log('📈 Calculating TOTAL across ALL filtered weeks');

    // Total Income = Income + Owner's Contributions
    const allIncomeRecords = data.filter(row =>
      row.parent_category === 'Income' || row.parent_category === "Owner's Contributions"
    );
    console.log('💵 Total Income records across all weeks:', allIncomeRecords.length);
    const currentIncome = allIncomeRecords.reduce((sum, row) => sum + (row.value || 0), 0);

    // Total Expense = Drawings + Fixed Cost + Transfers + Variable Cost + Credit Card
    const allExpenseRecords = data.filter(row =>
      row.parent_category === 'Drawings' ||
      row.parent_category === 'Fixed Cost' ||
      row.parent_category === 'Transfers' ||
      row.parent_category === 'Variable Cost' ||
      row.parent_category === 'Credit Card'
    );
    console.log('💸 Total Expense records across all weeks:', allExpenseRecords.length);
    const currentExpense = allExpenseRecords.reduce((sum, row) => sum + (row.value || 0), 0);

    // Net Income = Total Income + Total Expense
    const currentNetIncome = currentIncome + currentExpense;
    console.log('💰 TOTAL across filtered period - Income:', currentIncome, 'Expense:', currentExpense, 'Net:', currentNetIncome);

    // Calculate LAST WEEK KPIs (most recent end_date)
    let lastIncome = 0;
    let lastExpense = 0;
    let lastNetIncome = 0;

    const lastWeekData = data.filter(row => row.end_date === currentWeekEndDate);
    console.log('📉 Last week data:', lastWeekData.length, 'records for', currentWeekEndDate);

    // Total Income = Income + Owner's Contributions
    lastIncome = lastWeekData
      .filter(row => row.parent_category === 'Income' || row.parent_category === "Owner's Contributions")
      .reduce((sum, row) => sum + (row.value || 0), 0);

    // Total Expense = Drawings + Fixed Cost + Transfers + Variable Cost + Credit Card
    lastExpense = lastWeekData
      .filter(row =>
        row.parent_category === 'Drawings' ||
        row.parent_category === 'Fixed Cost' ||
        row.parent_category === 'Transfers' ||
        row.parent_category === 'Variable Cost' ||
        row.parent_category === 'Credit Card'
      )
      .reduce((sum, row) => sum + (row.value || 0), 0);

    // Net Income = Total Income + Total Expense
    lastNetIncome = lastIncome + lastExpense;
    console.log('💰 Last week totals - Income:', lastIncome, 'Expense:', lastExpense, 'Net:', lastNetIncome);

    // Calculate PREVIOUS WEEK KPIs (second most recent end_date)
    let prevIncome = 0;
    let prevExpense = 0;
    let prevNetIncome = 0;

    if (lastWeekEndDate) {
      const prevWeekData = data.filter(row => row.end_date === lastWeekEndDate);
      console.log('📉 Previous week data:', prevWeekData.length, 'records for', lastWeekEndDate);

      // Total Income = Income + Owner's Contributions
      prevIncome = prevWeekData
        .filter(row => row.parent_category === 'Income' || row.parent_category === "Owner's Contributions")
        .reduce((sum, row) => sum + (row.value || 0), 0);

      // Total Expense = Drawings + Fixed Cost + Transfers + Variable Cost + Credit Card
      prevExpense = prevWeekData
        .filter(row =>
          row.parent_category === 'Drawings' ||
          row.parent_category === 'Fixed Cost' ||
          row.parent_category === 'Transfers' ||
          row.parent_category === 'Variable Cost' ||
          row.parent_category === 'Credit Card'
        )
        .reduce((sum, row) => sum + (row.value || 0), 0);

      // Net Income = Total Income + Total Expense
      prevNetIncome = prevIncome + prevExpense;
      console.log('💰 Previous week totals - Income:', prevIncome, 'Expense:', prevExpense, 'Net:', prevNetIncome);
    }

    // Calculate Week over Week (WoW) percentages: Last Week vs Previous Week
    const incomeWoW = lastWeekEndDate && prevIncome !== 0 ? ((lastIncome - prevIncome) / Math.abs(prevIncome)) * 100 : 0;
    const expenseWoW = lastWeekEndDate && prevExpense !== 0 ? ((lastExpense - prevExpense) / Math.abs(prevExpense)) * 100 : 0;
    const netIncomeWoW = lastWeekEndDate && prevNetIncome !== 0 ? ((lastNetIncome - prevNetIncome) / Math.abs(prevNetIncome)) * 100 : 0;

    console.log('📊 WoW Comparison (Last vs Previous):', { incomeWoW, expenseWoW, netIncomeWoW });

    console.log('💰 KPIs:', {
      totalIncome: currentIncome,
      totalExpense: currentExpense,
      totalNetIncome: currentNetIncome,
      lastWeekIncome: lastIncome,
      lastWeekExpense: lastExpense,
      lastWeekNetIncome: lastNetIncome,
      prevWeekIncome: prevIncome,
      prevWeekExpense: prevExpense,
      prevWeekNetIncome: prevNetIncome
    });

    // Create new KPI object to force React re-render
    const newKpis = {
      currentWeek: {
        weekEndDate: currentWeekEndDate,
        income: currentIncome,
        expense: currentExpense,
        netIncome: currentNetIncome
      },
      lastWeek: {
        weekEndDate: currentWeekEndDate,
        income: lastIncome,
        expense: lastExpense,
        netIncome: lastNetIncome,
        incomeWoW: incomeWoW,
        expenseWoW: expenseWoW,
        netIncomeWoW: netIncomeWoW
      },
      previousWeek: lastWeekEndDate ? {
        weekEndDate: lastWeekEndDate,
        income: prevIncome,
        expense: prevExpense,
        netIncome: prevNetIncome
      } : null
    };

    setKpis(newKpis);
    console.log('✅ KPIs updated');
  };

  const prepareTrendData = (data) => {
    if (!data || data.length === 0) {
      setTrendData([]);
      return;
    }

    // Group by week and calculate totals using same logic as Balance Summary
    const weeklyTotals = {};

    data.forEach(row => {
      const week = row.end_date;
      if (!weeklyTotals[week]) {
        weeklyTotals[week] = { weekEndDate: week, income: 0, expense: 0 };
      }

      // Total Income = Income + Owner's Contributions
      if (row.parent_category === 'Income' || row.parent_category === "Owner's Contributions") {
        weeklyTotals[week].income += row.value || 0;
      }

      // Total Expense = Drawings + Fixed Cost + Transfers + Variable Cost + Credit Card
      if (row.parent_category === 'Drawings' ||
          row.parent_category === 'Fixed Cost' ||
          row.parent_category === 'Transfers' ||
          row.parent_category === 'Variable Cost' ||
          row.parent_category === 'Credit Card') {
        weeklyTotals[week].expense += row.value || 0;
      }
    });

    // Calculate net income and format for chart
    // Net Income = Total Income + Total Expense
    const chartData = Object.values(weeklyTotals)
      .map(week => ({
        week: formatDate(week.weekEndDate),
        Income: Math.round(week.income),
        Expense: Math.round(week.expense * -1), // Flip the sign for display
        'Net Income': Math.round(week.income + week.expense)
      }))
      .sort((a, b) => new Date(a.week) - new Date(b.week));

    setTrendData(chartData);
  };

  const calculateBalanceSummary = (data) => {
    console.log('🏦 Calculating Balance Summary');

    if (!data || data.length === 0) {
      console.log('❌ No data for balance summary');
      setBalanceSummary(null);
      return;
    }

    // Get unique weeks sorted chronologically (oldest first)
    const uniqueWeekEndDates = [...new Set(data.map(row => row.end_date))];
    const sortedWeeks = uniqueWeekEndDates.sort((a, b) => new Date(a) - new Date(b));

    if (sortedWeeks.length === 0) {
      setBalanceSummary(null);
      return;
    }

    // Build matrix data: each row is an item, columns are weeks
    const weeks = sortedWeeks.map(weekEnd => {
      const weekData = data.find(d => d.end_date === weekEnd);
      return {
        start_date: weekData?.start_date,
        end_date: weekEnd
      };
    });

    // Calculate values per week
    const weeklyValues = {};

    sortedWeeks.forEach(weekEnd => {
      const weekData = data.filter(row => row.end_date === weekEnd);

      // Get opening balance from "Cash at beginning of period" category in the weekly data
      const openingBalanceRecord = weekData.find(row =>
        row.category === 'Cash at beginning of period' ||
        row.parent_category === 'Cash at beginning of period'
      );
      const openingBalance = openingBalanceRecord ? (openingBalanceRecord.value || 0) : 0;

      // Calculate sub-totals for Income categories
      const income = weekData
        .filter(row => row.parent_category === 'Income')
        .reduce((sum, row) => sum + (row.value || 0), 0);
      const ownersContributions = weekData
        .filter(row => row.parent_category === "Owner's Contributions")
        .reduce((sum, row) => sum + (row.value || 0), 0);

      // Total Income = Income + Owner's Contributions
      const totalIncome = income + ownersContributions;

      // Calculate sub-totals for Expense categories
      const drawings = weekData
        .filter(row => row.parent_category === 'Drawings')
        .reduce((sum, row) => sum + (row.value || 0), 0);
      const fixedCost = weekData
        .filter(row => row.parent_category === 'Fixed Cost')
        .reduce((sum, row) => sum + (row.value || 0), 0);
      const transfers = weekData
        .filter(row => row.parent_category === 'Transfers')
        .reduce((sum, row) => sum + (row.value || 0), 0);
      const variableCost = weekData
        .filter(row => row.parent_category === 'Variable Cost')
        .reduce((sum, row) => sum + (row.value || 0), 0);
      const creditCard = weekData
        .filter(row => row.parent_category === 'Credit Card')
        .reduce((sum, row) => sum + (row.value || 0), 0);

      // Total Expense = Drawings + Fixed Cost + Transfers + Variable Cost + Credit Card
      const totalExpense = drawings + fixedCost + transfers + variableCost + creditCard;

      // Calculate Net Income = Total Income + Total Expense
      const netIncome = totalIncome + totalExpense;

      // Calculate Closing Balance
      const closingBalance = openingBalance + netIncome;

      weeklyValues[weekEnd] = {
        openingBalance,
        income,
        ownersContributions,
        totalIncome,
        drawings,
        fixedCost,
        transfers,
        variableCost,
        creditCard,
        totalExpense,
        netIncome,
        closingBalance
      };
    });

    // Calculate period totals
    const firstWeek = sortedWeeks[0];
    const lastWeek = sortedWeeks[sortedWeeks.length - 1];

    // Period opening balance is the first week's opening balance
    const periodOpeningBalance = weeklyValues[firstWeek]?.openingBalance || 0;

    // Period closing balance is the last week's closing balance
    const periodClosingBalance = weeklyValues[lastWeek]?.closingBalance || 0;

    // Period totals are sums across all weeks
    let periodTotalIncome = 0;
    let periodTotalExpense = 0;
    sortedWeeks.forEach(weekEnd => {
      periodTotalIncome += weeklyValues[weekEnd]?.totalIncome || 0;
      periodTotalExpense += weeklyValues[weekEnd]?.totalExpense || 0;
    });

    // Calculate period net income = Total Income + Total Expense
    const periodNetIncome = periodTotalIncome + periodTotalExpense;

    // Build category breakdowns for each parent category
    const income_breakdown = {};
    const owners_contributions_breakdown = {};
    const drawings_breakdown = {};
    const fixedCost_breakdown = {};
    const transfers_breakdown = {};
    const variableCost_breakdown = {};
    const creditCard_breakdown = {};

    data.forEach(row => {
      const { parent_category, category, end_date, value } = row;

      if (parent_category === 'Income') {
        if (!income_breakdown[category]) {
          income_breakdown[category] = {};
        }
        income_breakdown[category][end_date] = (income_breakdown[category][end_date] || 0) + (value || 0);
      } else if (parent_category === "Owner's Contributions") {
        if (!owners_contributions_breakdown[category]) {
          owners_contributions_breakdown[category] = {};
        }
        owners_contributions_breakdown[category][end_date] = (owners_contributions_breakdown[category][end_date] || 0) + (value || 0);
      } else if (parent_category === 'Drawings') {
        if (!drawings_breakdown[category]) {
          drawings_breakdown[category] = {};
        }
        drawings_breakdown[category][end_date] = (drawings_breakdown[category][end_date] || 0) + (value || 0);
      } else if (parent_category === 'Fixed Cost') {
        if (!fixedCost_breakdown[category]) {
          fixedCost_breakdown[category] = {};
        }
        fixedCost_breakdown[category][end_date] = (fixedCost_breakdown[category][end_date] || 0) + (value || 0);
      } else if (parent_category === 'Transfers') {
        if (!transfers_breakdown[category]) {
          transfers_breakdown[category] = {};
        }
        transfers_breakdown[category][end_date] = (transfers_breakdown[category][end_date] || 0) + (value || 0);
      } else if (parent_category === 'Variable Cost') {
        if (!variableCost_breakdown[category]) {
          variableCost_breakdown[category] = {};
        }
        variableCost_breakdown[category][end_date] = (variableCost_breakdown[category][end_date] || 0) + (value || 0);
      } else if (parent_category === 'Credit Card') {
        if (!creditCard_breakdown[category]) {
          creditCard_breakdown[category] = {};
        }
        creditCard_breakdown[category][end_date] = (creditCard_breakdown[category][end_date] || 0) + (value || 0);
      }
    });

    console.log('🏦 Balance Summary Matrix:', {
      weeks: sortedWeeks,
      weeklyValues,
      periodTotals: {
        openingBalance: periodOpeningBalance,
        totalIncome: periodTotalIncome,
        totalExpense: periodTotalExpense,
        netIncome: periodNetIncome,
        closingBalance: periodClosingBalance
      }
    });

    setBalanceSummary({
      weeks,
      weeklyValues,
      periodTotals: {
        openingBalance: periodOpeningBalance,
        totalIncome: periodTotalIncome,
        totalExpense: periodTotalExpense,
        netIncome: periodNetIncome,
        closingBalance: periodClosingBalance
      },
      income_breakdown,
      owners_contributions_breakdown,
      drawings_breakdown,
      fixedCost_breakdown,
      transfers_breakdown,
      variableCost_breakdown,
      creditCard_breakdown,
      periodStart: firstWeek,
      periodEnd: lastWeek
    });
  };

  const transformToMatrix = (data) => {
    if (!data || data.length === 0) {
      setMatrixData(null);
      return;
    }

    // Get unique weeks with both start and end dates, sorted by end_date descending (most recent first)
    const weeksMap = new Map();
    data.forEach(row => {
      if (!weeksMap.has(row.end_date)) {
        weeksMap.set(row.end_date, {
          start_date: row.start_date,
          end_date: row.end_date
        });
      }
    });

    // Sort ascending so oldest week is first (left side), newest is last (right side)
    const weeks = Array.from(weeksMap.values())
      .sort((a, b) => new Date(a.end_date) - new Date(b.end_date));

    // Group by parent_category and category
    const grouped = {};

    data.forEach(row => {
      const parentCategory = row.parent_category || 'Other';
      const category = row.category || 'Unknown';
      const weekEnd = row.end_date;
      const value = row.value || 0;
      const sortOrder = row.sort_order || 999;

      if (!grouped[parentCategory]) {
        grouped[parentCategory] = {
          sortOrder: sortOrder,
          categories: {}
        };
      }

      if (!grouped[parentCategory].categories[category]) {
        grouped[parentCategory].categories[category] = {};
      }

      // Initialize week value to 0 if not exists
      if (!grouped[parentCategory].categories[category][weekEnd]) {
        grouped[parentCategory].categories[category][weekEnd] = 0;
      }

      // Sum values for the same category in the same week (instead of overwriting)
      grouped[parentCategory].categories[category][weekEnd] += value;
    });

    setMatrixData({
      weeks,
      grouped
    });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Parse date without timezone conversion to avoid date shift issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatWeekRange = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    // Parse dates without timezone conversion
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  // Download entire page as PDF
  const downloadPageAsPDF = async () => {
    const element = document.querySelector('.weekly-data-matrix');

    // Save scroll position
    const originalScrollY = window.scrollY;
    const originalScrollX = window.scrollX;

    // Scroll to top-left before capture
    window.scrollTo(0, 0);

    // Find and handle scrollable containers
    const scrollContainers = element.querySelectorAll('.matrix-scroll');
    const scrollOriginalStyles = [];

    scrollContainers.forEach((container) => {
      scrollOriginalStyles.push({
        overflowX: container.style.overflowX,
        overflowY: container.style.overflowY,
        maxHeight: container.style.maxHeight
      });
      container.style.overflowX = 'visible';
      container.style.overflowY = 'visible';
      container.style.maxHeight = 'none';
    });

    // Temporarily remove sticky positioning and save original styles
    const stickyElements = element.querySelectorAll('.sticky-col, .sticky-col-2');
    const originalStyles = [];

    stickyElements.forEach((el) => {
      const computedWidth = el.offsetWidth;
      originalStyles.push({
        position: el.style.position,
        left: el.style.left,
        top: el.style.top,
        zIndex: el.style.zIndex,
        width: el.style.width,
        minWidth: el.style.minWidth
      });
      el.style.position = 'static';
      el.style.left = '';
      el.style.top = '';
      el.style.zIndex = '';
      // Preserve the width so category names don't get compressed
      el.style.width = computedWidth + 'px';
      el.style.minWidth = computedWidth + 'px';
    });

    // Small delay to let the layout settle
    await new Promise(resolve => setTimeout(resolve, 100));

    // Calculate dimensions to fit content
    const contentWidth = element.scrollWidth;
    const contentHeight = element.scrollHeight;

    // Use A3 landscape for wider content, or custom size if needed
    let format = 'a3';
    let customSize = null;

    // If content is wider than A3 landscape (~16.5 inches), use custom size
    if (contentWidth > 1584) { // A3 width in pixels at 96 DPI
      customSize = [contentWidth / 96 + 1, contentHeight / 96 + 1]; // Convert pixels to inches with padding
    }

    const opt = {
      margin: [0.3, 0.3, 0.3, 0.3],
      filename: `${period === 'm' ? 'Monthly' : 'Weekly'}_Cashflow_Report_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: {
        scale: 1.5,
        useCORS: true,
        logging: false,
        scrollY: 0,
        scrollX: 0,
        windowHeight: contentHeight,
        windowWidth: contentWidth
      },
      jsPDF: {
        unit: 'in',
        format: customSize || format,
        orientation: 'landscape',
        compress: true
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      // Restore scroll container styles
      scrollContainers.forEach((container, idx) => {
        container.style.overflowX = scrollOriginalStyles[idx].overflowX;
        container.style.overflowY = scrollOriginalStyles[idx].overflowY;
        container.style.maxHeight = scrollOriginalStyles[idx].maxHeight;
      });

      // Restore sticky positioning and original styles
      stickyElements.forEach((el, idx) => {
        el.style.position = originalStyles[idx].position;
        el.style.left = originalStyles[idx].left;
        el.style.top = originalStyles[idx].top;
        el.style.zIndex = originalStyles[idx].zIndex;
        el.style.width = originalStyles[idx].width;
        el.style.minWidth = originalStyles[idx].minWidth;
      });

      // Restore scroll position
      window.scrollTo(originalScrollX, originalScrollY);
    }
  };

  // Download Balance Summary and Matrix as Excel
  const downloadMatrixAsExcel = () => {
    if ((!balanceSummary || !balanceSummary.weeks) && (!matrixData || !matrixData.weeks)) {
      alert('No data available to export');
      return;
    }

    // Create workbook
    const wb = XLSX.utils.book_new();

    // ========== SHEET 1: BALANCE SUMMARY ==========
    if (balanceSummary && balanceSummary.weeks) {
      const balanceData = [];

      // Get last 10 weeks
      const last10Weeks = balanceSummary.weeks.slice(-10);

      // Header row
      const balanceHeader = ['Item', ...last10Weeks.map(week =>
        formatWeekRange(week.start_date, week.end_date)
      ), 'Period Total'];
      balanceData.push(balanceHeader);

      // Opening Balance
      const openingRow = ['Opening Balance', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.openingBalance || 0
      ), ''];
      balanceData.push(openingRow);

      // Total Income (Income + Owner's Contributions)
      let totalIncomeFor10Weeks = 0;
      last10Weeks.forEach(week => {
        totalIncomeFor10Weeks += balanceSummary.weeklyValues[week.end_date]?.totalIncome || 0;
      });
      const incomeRow = ['Total Income', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.totalIncome || 0
      ), totalIncomeFor10Weeks];
      balanceData.push(incomeRow);

      // Income (parent category sum)
      const incomeParentRow = ['  Income', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.income || 0
      ), ''];
      balanceData.push(incomeParentRow);

      // Owner's Contributions (parent category sum)
      const ownersContribRow = ['  Owner\'s Contributions', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.ownersContributions || 0
      ), ''];
      balanceData.push(ownersContribRow);

      // Total Expense (Drawings + Fixed Cost + Transfers + Variable Cost + Credit Card)
      let totalExpenseFor10Weeks = 0;
      last10Weeks.forEach(week => {
        totalExpenseFor10Weeks += balanceSummary.weeklyValues[week.end_date]?.totalExpense || 0;
      });
      const expenseRow = ['Total Expense', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.totalExpense || 0
      ), totalExpenseFor10Weeks];
      balanceData.push(expenseRow);

      // Drawings (parent category sum)
      const drawingsRow = ['  Drawings', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.drawings || 0
      ), ''];
      balanceData.push(drawingsRow);

      // Fixed Cost (parent category sum)
      const fixedCostRow = ['  Fixed Cost', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.fixedCost || 0
      ), ''];
      balanceData.push(fixedCostRow);

      // Transfers (parent category sum)
      const transfersRow = ['  Transfers', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.transfers || 0
      ), ''];
      balanceData.push(transfersRow);

      // Variable Cost (parent category sum)
      const variableCostRow = ['  Variable Cost', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.variableCost || 0
      ), ''];
      balanceData.push(variableCostRow);

      // Credit Card (parent category sum)
      const creditCardRow = ['  Credit Card', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.creditCard || 0
      ), ''];
      balanceData.push(creditCardRow);

      // Net Income
      const netIncomeRow = ['Net Income', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.netIncome || 0
      ), ''];
      balanceData.push(netIncomeRow);

      // Closing Balance
      const closingRow = ['Closing Balance', ...last10Weeks.map(week =>
        balanceSummary.weeklyValues[week.end_date]?.closingBalance || 0
      ), ''];
      balanceData.push(closingRow);

      const wsBalance = XLSX.utils.aoa_to_sheet(balanceData);
      const balanceColWidths = [{ wch: 30 }, ...last10Weeks.map(() => ({ wch: 15 })), { wch: 15 }];
      wsBalance['!cols'] = balanceColWidths;
      XLSX.utils.book_append_sheet(wb, wsBalance, 'Balance Summary');
    }

    // ========== SHEET 2: WEEKLY DATA MATRIX ==========
    if (matrixData && matrixData.weeks && matrixData.grouped) {
      const matrixExcelData = [];

      // Get last 10 weeks
      const last10MatrixWeeks = matrixData.weeks.slice(-10);

      // Header row
      const matrixHeader = ['Parent Category', 'Category', ...last10MatrixWeeks.map(week =>
        formatWeekRange(week.start_date, week.end_date)
      )];
      matrixExcelData.push(matrixHeader);

      // Data rows
      Object.keys(matrixData.grouped)
        .sort((a, b) => {
          const sortA = matrixData.grouped[a].sortOrder || 999;
          const sortB = matrixData.grouped[b].sortOrder || 999;
          return sortA - sortB;
        })
        .forEach(parentCategory => {
          const categories = Object.keys(matrixData.grouped[parentCategory].categories).sort();

          categories.forEach((category, idx) => {
            const row = [
              idx === 0 ? parentCategory : '', // Only show parent category on first row
              category,
              ...last10MatrixWeeks.map(week => {
                const value = matrixData.grouped[parentCategory].categories[category][week.end_date];
                return value !== undefined && value !== null ? value : 0;
              })
            ];
            matrixExcelData.push(row);
          });

          // Add total row for parent category (except for opening balance)
          if (parentCategory !== 'Cash at beginning of period' && parentCategory !== 'OpeningBalance') {
            const parentTotals = ['Total', parentCategory];
            last10MatrixWeeks.forEach(week => {
              let total = 0;
              categories.forEach(category => {
                total += matrixData.grouped[parentCategory].categories[category][week.end_date] || 0;
              });
              parentTotals.push(total);
            });
            matrixExcelData.push(parentTotals);
          }

          // Add empty row for spacing
          matrixExcelData.push([]);
        });

      const wsMatrix = XLSX.utils.aoa_to_sheet(matrixExcelData);
      const matrixColWidths = [
        { wch: 25 }, // Parent Category
        { wch: 30 }, // Category
        ...last10MatrixWeeks.map(() => ({ wch: 15 })) // Week columns
      ];
      wsMatrix['!cols'] = matrixColWidths;
      XLSX.utils.book_append_sheet(wb, wsMatrix, period === 'm' ? 'Monthly Data Matrix' : 'Weekly Data Matrix');
    }

    // Generate filename
    const companyName = viewMode === 'consolidated'
      ? parentCompanies.find(pc => pc.parent_company_id === selectedParentCompany)?.parent_company_name || 'Consolidated'
      : companies.find(c => c.company_id === selectedCompany)?.company_name || 'Company';

    const filename = `${period === 'm' ? 'Monthly' : 'Weekly'}_Report_${companyName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="weekly-data-matrix">
      <div className="matrix-header">
        <h2>{period === 'm' ? 'Monthly' : 'Weekly'} Cashflow Summary</h2>

        <div className="controls-row">
          <div className="controls-left">
            <div className="view-mode-selector">
              <label className={viewMode === 'consolidated' ? 'active' : ''}>
                <input
                  type="radio"
                  value="consolidated"
                  checked={viewMode === 'consolidated'}
                  onChange={(e) => setViewMode(e.target.value)}
                />
                <span>Consolidated</span>
              </label>
              <label className={viewMode === 'single' ? 'active' : ''}>
                <input
                  type="radio"
                  value="single"
                  checked={viewMode === 'single'}
                  onChange={(e) => setViewMode(e.target.value)}
                />
                <span>Single Company</span>
              </label>
            </div>

            <div className="view-mode-selector">
              <label className={period === 'w' ? 'active' : ''}>
                <input
                  type="radio"
                  value="w"
                  checked={period === 'w'}
                  onChange={(e) => setPeriod(e.target.value)}
                />
                <span>Weekly</span>
              </label>
              <label className={period === 'm' ? 'active' : ''}>
                <input
                  type="radio"
                  value="m"
                  checked={period === 'm'}
                  onChange={(e) => setPeriod(e.target.value)}
                />
                <span>Monthly</span>
              </label>
            </div>

            {viewMode === 'consolidated' && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                <select
                  className="company-selector"
                  value={selectedParentCompany}
                  onChange={(e) => setSelectedParentCompany(e.target.value)}
                >
                  <option value="">-- Select Parent Company --</option>
                  {parentCompanies.map(pc => (
                    <option key={pc.parent_company_id} value={pc.parent_company_id}>
                      {pc.parent_company_name}
                    </option>
                  ))}
                </select>
                {selectedParentCompany && companies.filter(c => c.parent_company_id === selectedParentCompany).length > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      fontSize: '0.75rem',
                      color: '#9CA3AF',
                      fontWeight: '500'
                    }}>
                      {companies.filter(c => c.parent_company_id === selectedParentCompany).length} companies:
                    </span>
                    {companies
                      .filter(c => c.parent_company_id === selectedParentCompany)
                      .map(c => (
                        <span
                          key={c.company_id}
                          style={{
                            fontSize: '0.75rem',
                            color: '#4B5563',
                            background: '#F3F4F6',
                            padding: '2px 10px',
                            borderRadius: '4px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}
                          title={c.company_name}
                        >
                          {c.company_name}
                        </span>
                      ))
                    }
                  </div>
                )}
              </div>
            )}

            {viewMode === 'single' && (
              <select
                className="company-selector"
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
              >
                <option value="">-- Select Company --</option>
                {companies.map(company => (
                  <option key={company.company_id} value={company.company_id}>
                    {company.company_name}
                    {company.parent_company_name && ` (${company.parent_company_name})`}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              className="btn-primary btn-load"
              onClick={loadWeeklyData}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load Data'}
            </button>

            {weeklyData.length > 0 && (
              <button
                className="btn-secondary"
                onClick={downloadPageAsPDF}
                style={{
                  background: '#10B981',
                  color: 'white',
                  border: 'none'
                }}
                title="Download entire page as PDF"
              >
                Download PDF
              </button>
            )}
          </div>
        </div>

        {/* Week End Date Filters */}
        {weeklyData.length > 0 && (
          <div className="filters-row" style={{ marginTop: '12px', padding: '12px 16px', background: '#F9FAFB', borderRadius: '6px' }}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Dropdown Week End Filter */}
              <div className="filter-group">
                <label htmlFor="weekEndFilter" style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  Filter by {period === 'm' ? 'Month' : 'Week'} End:
                </label>
                <select
                  id="weekEndFilter"
                  multiple
                  size="1"
                  value={selectedWeekEnds}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setSelectedWeekEnds(selected);
                    setLastNWeeks('all');
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #D1D5DB',
                    fontSize: '13px',
                    fontWeight: '400',
                    cursor: 'pointer',
                    background: '#F9FAFB',
                    minWidth: '200px'
                  }}
                >
                  <option value="">All {period === 'm' ? 'Months' : 'Weeks'}</option>
                  {getUniqueWeekEnds().map((weekEnd) => (
                    <option key={weekEnd} value={weekEnd}>
                      {formatDate(weekEnd)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quick Filters */}
              <div className="filter-group">
                <label htmlFor="lastNWeeksFilter" style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  Quick Filter:
                </label>
                <select
                  id="lastNWeeksFilter"
                  value={lastNWeeks}
                  onChange={(e) => {
                    setLastNWeeks(e.target.value);
                    setSelectedWeekEnds([]);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #D1D5DB',
                    fontSize: '13px',
                    fontWeight: '400',
                    cursor: 'pointer',
                    background: '#F9FAFB',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All {period === 'm' ? 'Months' : 'Weeks'}</option>
                  <option value="5">Last 5</option>
                  <option value="10">Last 10</option>
                  <option value="15">Last 15</option>
                  <option value="20">Last 20</option>
                  <option value="30">Last 30</option>
                </select>
              </div>

              {/* Clear Button and Counter */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: 'auto' }}>
                {(selectedWeekEnds.length > 0 || lastNWeeks !== 'all') && (
                  <button
                    onClick={() => {
                      setSelectedWeekEnds([]);
                      setLastNWeeks('all');
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '4px',
                      border: 'none',
                      background: '#FEE2E2',
                      color: '#DC2626',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '13px'
                    }}
                  >
                    Clear Filters
                  </button>
                )}
                <div style={{
                  color: '#6B7280',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  Showing {filteredWeeklyData.length} of {weeklyData.length} records
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {kpis && (
        <div className="kpis-section">
          <div className="kpis-row">
            {/* {period === 'm' ? 'Monthly' : 'Weekly'} Total KPIs */}
            <div className="kpi-card income">
              <div className="kpi-header">
                <div className="kpi-label">Total Income</div>
              </div>
              <div className="kpi-value">{formatCurrency(kpis.currentWeek.income)}</div>
              <div className="kpi-period">{period === 'm' ? 'Monthly' : 'Weekly'} Total</div>
            </div>

            <div className="kpi-card expense">
              <div className="kpi-header">
                <div className="kpi-label">Total Expense</div>
              </div>
              <div className="kpi-value">{formatCurrency(kpis.currentWeek.expense)}</div>
              <div className="kpi-period">{period === 'm' ? 'Monthly' : 'Weekly'} Total</div>
            </div>

            <div className="kpi-card net-income">
              <div className="kpi-header">
                <div className="kpi-label">Net Income</div>
              </div>
              <div className="kpi-value">{formatCurrency(kpis.currentWeek.netIncome)}</div>
              <div className="kpi-period">{period === 'm' ? 'Monthly' : 'Weekly'} Total</div>
            </div>

            {/* Last Week KPIs with WoW comparison */}
            {kpis.lastWeek && (
              <>
                <div className="kpi-card income-last">
                  <div className="kpi-header">
                    <div className="kpi-label">Last {period === 'm' ? 'Month' : 'Week'} Income</div>
                    {kpis.previousWeek && (
                      <div className={`kpi-wow ${kpis.lastWeek.incomeWoW >= 0 ? 'positive' : 'negative'}`}>
                        {kpis.lastWeek.incomeWoW >= 0 ? '↑' : '↓'} {Math.abs(kpis.lastWeek.incomeWoW).toFixed(1)}%
                      </div>
                    )}
                  </div>
                  <div className="kpi-value">{formatCurrency(kpis.lastWeek.income)}</div>
                  <div className="kpi-period">{formatDate(kpis.lastWeek.weekEndDate)}</div>
                </div>

                <div className="kpi-card expense-last">
                  <div className="kpi-header">
                    <div className="kpi-label">Last {period === 'm' ? 'Month' : 'Week'} Expense</div>
                    {kpis.previousWeek && (
                      <div className={`kpi-wow ${kpis.lastWeek.expenseWoW >= 0 ? 'negative' : 'positive'}`}>
                        {kpis.lastWeek.expenseWoW >= 0 ? '↑' : '↓'} {Math.abs(kpis.lastWeek.expenseWoW).toFixed(1)}%
                      </div>
                    )}
                  </div>
                  <div className="kpi-value">{formatCurrency(kpis.lastWeek.expense)}</div>
                  <div className="kpi-period">{formatDate(kpis.lastWeek.weekEndDate)}</div>
                </div>

                <div className="kpi-card net-income-last">
                  <div className="kpi-header">
                    <div className="kpi-label">Last {period === 'm' ? 'Month' : 'Week'} Net Income</div>
                    {kpis.previousWeek && (
                      <div className={`kpi-wow ${kpis.lastWeek.netIncomeWoW >= 0 ? 'positive' : 'negative'}`}>
                        {kpis.lastWeek.netIncomeWoW >= 0 ? '↑' : '↓'} {Math.abs(kpis.lastWeek.netIncomeWoW).toFixed(1)}%
                      </div>
                    )}
                  </div>
                  <div className="kpi-value">{formatCurrency(kpis.lastWeek.netIncome)}</div>
                  <div className="kpi-period">{formatDate(kpis.lastWeek.weekEndDate)}</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Trend Line Chart */}
      {/* {trendData.length > 0 && (
        <div className="chart-section">
          <h3>Weekly Trend</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={trendData}>
              <CartesianGrid stroke="#e0e0e0" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="Income" stroke="#4CAF50" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="Expense" stroke="#f44336" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="Net Income" stroke="#2196F3" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )} */}

      {/* Balance Summary Matrix */}
      {balanceSummary && (
        <div className="balance-summary-section" style={{ margin: '24px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: '#111827' }}>Balance Summary</h3>
            <button
              onClick={() => setShowDetailedBalance(!showDetailedBalance)}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: '1px solid #1F2937',
                background: showDetailedBalance ? '#1F2937' : 'transparent',
                color: showDetailedBalance ? 'white' : '#1F2937',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              {showDetailedBalance ? 'Hide Details' : 'Show Details'}
            </button>
          </div>
          <div className="matrix-container">
            <div className="matrix-scroll" ref={balanceSummaryScrollRef} style={{ overflowY: 'visible', maxHeight: 'none' }}>
              <table className="matrix-table">
                <thead>
                  <tr>
                    <th className="sticky-col header-col">Item</th>
                    {balanceSummary.weeks.map((week, index) => {
                      const isLastWeek = index === balanceSummary.weeks.length - 1;
                      return (
                        <th
                          key={week.end_date}
                          className={`week-col ${isLastWeek ? 'latest-week' : ''}`}
                          style={isLastWeek ? {
                            background: '#DBEAFE',
                            fontWeight: '700',
                            borderBottom: '3px solid #2563EB'
                          } : {}}
                        >
                          {formatWeekRange(week.start_date, week.end_date)}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* Opening Balance Row */}
                  <tr>
                    <td className="sticky-col category" style={{ fontWeight: '600' }}>
                      Opening Balance
                    </td>
                    {balanceSummary.weeks.map((week, index) => {
                      const isLastWeek = index === balanceSummary.weeks.length - 1;
                      return (
                        <td
                          key={week.end_date}
                          className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                          style={isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}}
                        >
                          {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.openingBalance)}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Income Sub-category */}
                  {showDetailedBalance && (
                    <tr style={{ background: '#DCFCE7' }}>
                      <td className="sticky-col category" style={{ paddingLeft: '20px', color: '#059669' }}>
                        Income
                      </td>
                      {balanceSummary.weeks.map((week, index) => {
                        const isLastWeek = index === balanceSummary.weeks.length - 1;
                        return (
                          <td
                            key={week.end_date}
                            className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                            style={{
                              ...(isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}),
                              color: '#059669'
                            }}
                          >
                            {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.income)}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {/* Owner's Contributions Sub-category */}
                  {showDetailedBalance && (
                    <tr style={{ background: '#DCFCE7' }}>
                      <td className="sticky-col category" style={{ paddingLeft: '20px', color: '#059669' }}>
                        Owner's Contributions
                      </td>
                      {balanceSummary.weeks.map((week, index) => {
                        const isLastWeek = index === balanceSummary.weeks.length - 1;
                        return (
                          <td
                            key={week.end_date}
                            className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                            style={{
                              ...(isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}),
                              color: '#059669'
                            }}
                          >
                            {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.ownersContributions)}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {/* Total Income Row */}
                  <tr style={{ background: '#DCFCE7' }}>
                    <td className="sticky-col category" style={{ fontWeight: '600', color: '#047857' }}>
                      Total Income
                    </td>
                    {balanceSummary.weeks.map((week, index) => {
                      const isLastWeek = index === balanceSummary.weeks.length - 1;
                      return (
                        <td
                          key={week.end_date}
                          className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                          style={{
                            ...(isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}),
                            color: '#047857',
                            fontWeight: '600'
                          }}
                        >
                          {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.totalIncome)}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Drawings Sub-category */}
                  {showDetailedBalance && (
                    <tr style={{ background: '#FEE2E2' }}>
                      <td className="sticky-col category" style={{ paddingLeft: '20px', color: '#DC2626' }}>
                        Drawings
                      </td>
                      {balanceSummary.weeks.map((week, index) => {
                        const isLastWeek = index === balanceSummary.weeks.length - 1;
                        return (
                          <td
                            key={week.end_date}
                            className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                            style={{
                              ...(isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}),
                              color: '#DC2626'
                            }}
                          >
                            {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.drawings)}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {/* Fixed Cost Sub-category */}
                  {showDetailedBalance && (
                    <tr style={{ background: '#FEE2E2' }}>
                      <td className="sticky-col category" style={{ paddingLeft: '20px', color: '#DC2626' }}>
                        Fixed Cost
                      </td>
                      {balanceSummary.weeks.map((week, index) => {
                        const isLastWeek = index === balanceSummary.weeks.length - 1;
                        return (
                          <td
                            key={week.end_date}
                            className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                            style={{
                              ...(isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}),
                              color: '#DC2626'
                            }}
                          >
                            {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.fixedCost)}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {/* Transfers Sub-category */}
                  {showDetailedBalance && (
                    <tr style={{ background: '#FEE2E2' }}>
                      <td className="sticky-col category" style={{ paddingLeft: '20px', color: '#DC2626' }}>
                        Transfers
                      </td>
                      {balanceSummary.weeks.map((week, index) => {
                        const isLastWeek = index === balanceSummary.weeks.length - 1;
                        return (
                          <td
                            key={week.end_date}
                            className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                            style={{
                              ...(isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}),
                              color: '#DC2626'
                            }}
                          >
                            {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.transfers)}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {/* Variable Cost Sub-category */}
                  {showDetailedBalance && (
                    <tr style={{ background: '#FEE2E2' }}>
                      <td className="sticky-col category" style={{ paddingLeft: '20px', color: '#DC2626' }}>
                        Variable Cost
                      </td>
                      {balanceSummary.weeks.map((week, index) => {
                        const isLastWeek = index === balanceSummary.weeks.length - 1;
                        return (
                          <td
                            key={week.end_date}
                            className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                            style={{
                              ...(isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}),
                              color: '#DC2626'
                            }}
                          >
                            {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.variableCost)}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {/* Credit Card Sub-category */}
                  {showDetailedBalance && (
                    <tr style={{ background: '#FEE2E2' }}>
                      <td className="sticky-col category" style={{ paddingLeft: '20px', color: '#DC2626' }}>
                        Credit Card
                      </td>
                      {balanceSummary.weeks.map((week, index) => {
                        const isLastWeek = index === balanceSummary.weeks.length - 1;
                        return (
                          <td
                            key={week.end_date}
                            className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                            style={{
                              ...(isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}),
                              color: '#DC2626'
                            }}
                          >
                            {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.creditCard)}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {/* Total Expense Row */}
                  <tr style={{ background: '#FEE2E2' }}>
                    <td className="sticky-col category" style={{ fontWeight: '600', color: '#B91C1C' }}>
                      Total Expense
                    </td>
                    {balanceSummary.weeks.map((week, index) => {
                      const isLastWeek = index === balanceSummary.weeks.length - 1;
                      return (
                        <td
                          key={week.end_date}
                          className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                          style={{
                            ...(isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}),
                            color: '#B91C1C',
                            fontWeight: '600'
                          }}
                        >
                          {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.totalExpense)}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Net Income Row */}
                  <tr>
                    <td className="sticky-col category" style={{ fontWeight: '600' }}>
                      Net Income
                    </td>
                    {balanceSummary.weeks.map((week, index) => {
                      const isLastWeek = index === balanceSummary.weeks.length - 1;
                      const netIncome = balanceSummary.weeklyValues[week.end_date]?.netIncome || 0;
                      return (
                        <td
                          key={week.end_date}
                          className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                          style={{
                            ...(isLastWeek ? { background: '#DBEAFE', fontWeight: '600' } : {}),
                            color: netIncome >= 0 ? '#047857' : '#B91C1C',
                            fontWeight: '600'
                          }}
                        >
                          {formatCurrency(netIncome)}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Closing Balance Row */}
                  <tr style={{ background: '#DBEAFE' }}>
                    <td className="sticky-col category" style={{
                      fontWeight: '700',
                      color: '#1D4ED8'
                    }}>
                      Closing Balance
                    </td>
                    {balanceSummary.weeks.map((week, index) => {
                      const isLastWeek = index === balanceSummary.weeks.length - 1;
                      return (
                        <td
                          key={week.end_date}
                          className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                          style={{
                            background: '#DBEAFE',
                            fontWeight: '700',
                            color: '#1D4ED8'
                          }}
                        >
                          {formatCurrency(balanceSummary.weeklyValues[week.end_date]?.closingBalance)}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {matrixData && (
        <div style={{ margin: '24px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: '#111827' }}>{period === 'm' ? 'Monthly' : 'Weekly'} Data Matrix</h3>
            <button
              onClick={downloadMatrixAsExcel}
              style={{
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                background: '#059669',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title="Download Balance Summary and Matrix as Excel file"
            >
              Download Excel
            </button>
          </div>
          <div className="matrix-container">
            <div className="matrix-scroll" ref={matrixScrollRef} style={{ overflowY: 'visible', maxHeight: 'none' }}>
              <table className="matrix-table">
                <thead>
                  <tr>
                    <th className="sticky-col header-col" rowSpan="2">Parent Category</th>
                    <th className="sticky-col-2 header-col" rowSpan="2">Category</th>
                    <th className="week-header" colSpan={matrixData.weeks.length}>
                      {period === 'm' ? 'Month' : 'Week'} Period
                    </th>
                  </tr>
                <tr>
                  {matrixData.weeks.map((week, index) => {
                    const isLastWeek = index === matrixData.weeks.length - 1;
                    return (
                      <th
                        key={week.end_date}
                        className={`week-col ${isLastWeek ? 'latest-week' : ''}`}
                        style={isLastWeek ? {
                          background: '#DBEAFE',
                          fontWeight: '700',
                          borderBottom: '3px solid #2563EB'
                        } : {}}
                      >
                        {formatWeekRange(week.start_date, week.end_date)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {Object.keys(matrixData.grouped)
                  .sort((a, b) => {
                    const sortA = matrixData.grouped[a].sortOrder || 999;
                    const sortB = matrixData.grouped[b].sortOrder || 999;
                    return sortA - sortB;
                  })
                  .map(parentCategory => {
                    const categories = Object.keys(matrixData.grouped[parentCategory].categories).sort();

                    // Calculate totals per week for this parent category
                    const parentTotals = {};
                    matrixData.weeks.forEach(week => {
                      let total = 0;
                      categories.forEach(category => {
                        total += matrixData.grouped[parentCategory].categories[category][week.end_date] || 0;
                      });
                      parentTotals[week.end_date] = total;
                    });

                    return (
                      <>
                        {categories.map((category, idx) => (
                          <tr key={`${parentCategory}-${category}`}>
                            {idx === 0 && (
                              <td
                                className="sticky-col parent-category"
                                rowSpan={categories.length}
                              >
                                {parentCategory}
                              </td>
                            )}
                            <td className="sticky-col-2 category">
                              {category}
                            </td>
                            {matrixData.weeks.map((week, index) => {
                              const isLastWeek = index === matrixData.weeks.length - 1;
                              return (
                                <td
                                  key={week.end_date}
                                  className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                                  style={isLastWeek ? {
                                    background: '#DBEAFE',
                                    fontWeight: '600'
                                  } : {}}
                                >
                                  {formatCurrency(matrixData.grouped[parentCategory].categories[category][week.end_date])}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        {/* Parent Category Total Row - Skip for Opening Balance */}
                        {parentCategory !== 'Cash at beginning of period' &&
                         parentCategory !== 'OpeningBalance' && (
                          <tr key={`${parentCategory}-total`} style={{ background: '#F9FAFB', borderTop: '2px solid #E5E7EB' }}>
                            <td
                              className="sticky-col parent-category"
                              style={{ fontWeight: '700', background: '#F9FAFB' }}
                            >
                              Total
                            </td>
                            <td className="sticky-col-2 category" style={{ fontWeight: '700', background: '#F9FAFB' }}>
                              {parentCategory}
                            </td>
                            {matrixData.weeks.map((week, index) => {
                              const isLastWeek = index === matrixData.weeks.length - 1;
                              return (
                                <td
                                  key={week.end_date}
                                  className={`amount-cell ${isLastWeek ? 'latest-week-cell' : ''}`}
                                  style={{
                                    fontWeight: '700',
                                    background: isLastWeek ? '#EFF6FF' : '#F9FAFB'
                                  }}
                                >
                                  {formatCurrency(parentTotals[week.end_date])}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                      </>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      )}

      {!matrixData && !loading && (
        <div className="empty-state">
          <p>
            {viewMode === 'consolidated'
              ? 'Select a parent company and click "Load Data" to view consolidated data for all companies under that parent'
              : 'Select a company and click "Load Data" to view the financial matrix'}
          </p>
        </div>
      )}
    </div>
  );
}

export default WeeklyDataMatrix;
